import {
  type AtticApiKeyFragment,
  AtticApiKeyStatusEnum,
  type AtticApiKeyUpdateInput,
  type AtticApiKeyUsageDayFragment,
  PermissionEnum,
  type PermissionFragment,
  type UserPermissionFragment,
} from "@dashboard/graphql";
import { type PermissionData } from "@dashboard/permissionGroups/components/PermissionGroupDetailsPage/PermissionGroupDetailsPage";

import { type AtticApiKeyFormData } from "./types";

/** The backend's limits (api `attic/apikeys`). */
export const LABEL_MAX_LENGTH = 50;
export const MAX_SECRETS = 2;
/** A key this long without a request is flagged: likely forgotten, still live. */
export const STALE_AFTER_DAYS = 90;
/** The usage card's window, in UTC days, today included. */
export const USAGE_WINDOW_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * True for a key's GraphQL id: base64 of "AtticApiKey:<n>". The backend fails
 * the query on an id that is not one rather than finding nothing, so the
 * detail page does not ask. As in the activity log, the id must be canonical
 * padded base64: the browser's atob forgives what the backend's decode does not.
 */
export const isApiKeyGlobalId = (nodeId: string): boolean => {
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(nodeId) || nodeId.length % 4 !== 0) {
    return false;
  }

  try {
    return /^AtticApiKey:\d+$/.test(atob(nodeId));
  } catch {
    return false;
  }
};

/** MANAGE_APPS is never allowed on a key: a key could otherwise mint keys. */
export const isGrantableToApiKey = (code: PermissionEnum) => code !== PermissionEnum.MANAGE_APPS;

/**
 * The permission picker's choices: every store permission a key may hold,
 * with the ones the viewer does not hold disabled (granting one is refused
 * with OUT_OF_SCOPE_PERMISSION).
 */
export const getApiKeyPermissionChoices = (
  shopPermissions: PermissionFragment[],
  userPermissions: UserPermissionFragment[],
): PermissionData[] => {
  const held = new Set(userPermissions.map(permission => permission.code));

  return shopPermissions
    .filter(permission => isGrantableToApiKey(permission.code))
    .map(permission => ({
      code: permission.code,
      name: permission.name,
      disabled: !held.has(permission.code),
      lastSource: false,
    }));
};

/** True when the key may do something the viewer may not, so they cannot change it. */
export const exceedsViewerPermissions = (
  key: Pick<AtticApiKeyFragment, "permissions">,
  userPermissions: UserPermissionFragment[],
): boolean => {
  const held = new Set(userPermissions.map(permission => permission.code));

  return key.permissions.some(permission => !held.has(permission.code));
};

/** Midnight at the start of a YYYY-MM-DD day in the viewer's timezone, or null for no real day. */
const localDayStart = (day: string): Date | null => {
  const match = DAY.exec(day);

  if (!match) {
    return null;
  }

  const [year, month, date] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const start = new Date(year, month, date);

  if (start.getFullYear() !== year || start.getMonth() !== month || start.getDate() !== date) {
    return null;
  }

  return start;
};

const pad = (value: number) => String(value).padStart(2, "0");

const toLocalDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const toUtcDay = (date: Date) =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

/**
 * The expiry picker works in calendar days: a key set to expire on a day works
 * through the end of that day in the viewer's timezone, so it stops at the
 * next midnight. Empty means never; null for something that is not a day.
 */
export const expiryDayToIso = (day: string): string | null => {
  const start = localDayStart(day);

  if (!start) {
    return null;
  }

  start.setDate(start.getDate() + 1);

  return start.toISOString();
};

/**
 * The calendar day an expiry instant falls at the end of: the inverse of
 * expiryDayToIso, so an unchanged day round-trips. An expiry set by the API at
 * 15:00 shows that day too. Empty for none.
 */
export const expiryIsoToDay = (iso: string | null | undefined): string => {
  if (!iso) {
    return "";
  }

  const instant = new Date(iso);

  if (Number.isNaN(instant.getTime())) {
    return "";
  }

  return toLocalDay(new Date(instant.getTime() - 1));
};

/**
 * True when the date input inside `container` holds text it cannot read. The
 * browser reports such an input's value as "", exactly like an empty one, and
 * typing into it may fire no change event at all, so this is read from the
 * input at the moment of Save. A cleared field is valid and returns false: it
 * means "no expiry".
 */
export const isExpiryInputUnreadable = (container: HTMLElement | null): boolean => {
  const input = container?.querySelector<HTMLInputElement>('input[type="date"]');
  const validity = input?.validity;

  if (!input || !validity) {
    return false;
  }

  return validity.badInput || (!validity.valid && input.value === "");
};

/** Today in the viewer's timezone, as the picker's earliest day. */
export const todayLocalDay = (now: number) => toLocalDay(new Date(now));

/**
 * A key nobody has used for a while is a credential left lying around: flag
 * one whose last request was STALE_AFTER_DAYS or more ago, or that has never
 * made one and was issued that long ago. Only an ACTIVE key is flagged. `lastUsedOn` is a UTC day.
 */
export const isStaleApiKey = (
  key: Pick<AtticApiKeyFragment, "status" | "lastUsedOn" | "createdAt">,
  now: number,
): boolean => {
  // Only a working key is a live credential: an expired one cannot
  // authenticate (until someone extends it) and a revoked one never will.
  if (key.status !== AtticApiKeyStatusEnum.ACTIVE) {
    return false;
  }

  // A last-used day counts from its END: a request may have come at 23:59
  // UTC, so the day's start would flag the key up to a day early.
  const since = key.lastUsedOn
    ? Date.parse(`${key.lastUsedOn}T00:00:00Z`) + DAY_MS
    : Date.parse(key.createdAt);

  if (Number.isNaN(since)) {
    return false;
  }

  return now - since >= STALE_AFTER_DAYS * DAY_MS;
};

/** The usage card's window: the last USAGE_WINDOW_DAYS UTC days, today included. */
export const getUsageWindow = (now: number) => {
  const until = new Date(now);
  const since = new Date(now - (USAGE_WINDOW_DAYS - 1) * DAY_MS);

  return { since: toUtcDay(since), until: toUtcDay(until) };
};

export interface AtticApiKeyUsageRow {
  day: string;
  requests: number;
  /** Client (4xx other than 429) and server (5xx) errors together. */
  errors: number;
  throttled: number;
  cost: number;
  /** Mean latency in ms, or null for a day with no requests. */
  meanLatencyMs: number | null;
}

const toRow = (
  day: string,
  total: AtticApiKeyUsageDayFragment | undefined,
): AtticApiKeyUsageRow => ({
  day,
  requests: total?.requests ?? 0,
  errors: (total?.clientErrors ?? 0) + (total?.serverErrors ?? 0),
  throttled: total?.throttled ?? 0,
  cost: total?.cost ?? 0,
  meanLatencyMs: total && total.requests > 0 ? total.latencyMs / total.requests : null,
});

/**
 * One row per UTC day of the window, oldest first. The API leaves out a day
 * with no requests; this puts it back at zero, so a quiet day reads as quiet
 * rather than as missing.
 */
export const getUsageRows = (
  usage: AtticApiKeyUsageDayFragment[],
  window: { since: string; until: string },
): AtticApiKeyUsageRow[] => {
  const byDay = new Map(usage.map(total => [String(total.day), total]));
  const rows: AtticApiKeyUsageRow[] = [];
  const end = Date.parse(`${window.until}T00:00:00Z`);

  for (let at = Date.parse(`${window.since}T00:00:00Z`); at <= end; at += DAY_MS) {
    const day = toUtcDay(new Date(at));

    rows.push(toRow(day, byDay.get(day)));
  }

  return rows;
};

/** The window's totals; the mean latency is over every request, not a mean of means. */
export const getUsageTotals = (
  usage: AtticApiKeyUsageDayFragment[],
): Omit<AtticApiKeyUsageRow, "day"> => {
  const requests = usage.reduce((sum, total) => sum + total.requests, 0);
  const latency = usage.reduce((sum, total) => sum + total.latencyMs, 0);

  return {
    requests,
    errors: usage.reduce((sum, total) => sum + total.clientErrors + total.serverErrors, 0),
    throttled: usage.reduce((sum, total) => sum + total.throttled, 0),
    cost: usage.reduce((sum, total) => sum + total.cost, 0),
    meanLatencyMs: requests > 0 ? latency / requests : null,
  };
};

/** What the holder sends with every request. */
export const getAuthorizationHeader = (secret: string) => `Authorization: Bearer ${secret}`;

/** The pill colour for a status: works, needs attention, final. */
export const getStatusPillColor = (status: AtticApiKeyStatusEnum) => {
  switch (status) {
    case AtticApiKeyStatusEnum.ACTIVE:
      return "success" as const;
    case AtticApiKeyStatusEnum.EXPIRED:
      return "warning" as const;
    default:
      return "error" as const;
  }
};

/** A key's fields as its edit form holds them. */
export const apiKeyToFormData = (apiKey: AtticApiKeyFragment): AtticApiKeyFormData => ({
  label: apiKey.label,
  expiryDay: expiryIsoToDay(apiKey.expiresAt),
  readOnly: apiKey.readOnly,
  permissions: apiKey.permissions.map(permission => permission.code),
});

const samePermissions = (a: PermissionEnum[], b: PermissionEnum[]) =>
  [...a].sort().join() === [...b].sort().join();

/**
 * The form after the key is re-read (a save, an added secret, a status that
 * changed under an open page): each field still showing the key's previous
 * value takes the new one, and each field the merchant has edited keeps the
 * edit. Re-mounting the page instead would silently drop unsaved edits.
 */
export const syncFormWithKey = (
  current: AtticApiKeyFormData,
  previous: AtticApiKeyFormData,
  next: AtticApiKeyFormData,
): AtticApiKeyFormData => ({
  label: current.label === previous.label ? next.label : current.label,
  expiryDay: current.expiryDay === previous.expiryDay ? next.expiryDay : current.expiryDay,
  readOnly: current.readOnly === previous.readOnly ? next.readOnly : current.readOnly,
  permissions: samePermissions(current.permissions, previous.permissions)
    ? next.permissions
    : current.permissions,
});

/** True when the form differs from the key as saved. */
export const isApiKeyFormDirty = (apiKey: AtticApiKeyFragment, data: AtticApiKeyFormData) => {
  const initial = apiKeyToFormData(apiKey);

  // Compared trimmed, as it is sent: "Label " saved as "Label" is not a change.
  return (
    data.label.trim() !== initial.label ||
    data.expiryDay !== initial.expiryDay ||
    data.readOnly !== initial.readOnly ||
    !samePermissions(data.permissions, initial.permissions)
  );
};

/**
 * What a save sends: only the fields that changed, so a save never reverts a
 * rename or a permission change someone else made meanwhile. An expiry left as
 * it was is not re-sent either, because sending one at all (even null) is what
 * reactivates an expired key.
 *
 * Null when the changed expiry is not a day this can read (a two-digit year
 * typed into the picker arrives as year 0026): sending nothing is the only
 * safe answer, since null would clear the expiry and revive an expired key.
 */
export const getUpdateInput = (
  apiKey: AtticApiKeyFragment,
  data: AtticApiKeyFormData,
  expiryUnreadable = false,
): AtticApiKeyUpdateInput | null => {
  // A half-typed date reads as "": that is not "no expiry", so nothing is sent.
  if (expiryUnreadable) {
    return null;
  }

  const initial = apiKeyToFormData(apiKey);
  const input: AtticApiKeyUpdateInput = {};
  const label = data.label.trim();

  if (label !== initial.label) {
    input.label = label;
  }

  if (!samePermissions(data.permissions, initial.permissions)) {
    input.permissions = data.permissions;
  }

  // Sent only when switched, like the rest: omitted leaves it as it is.
  if (data.readOnly !== initial.readOnly) {
    input.readOnly = data.readOnly;
  }

  if (data.expiryDay !== initial.expiryDay) {
    if (!data.expiryDay) {
      input.expiresAt = null;
    } else {
      const expiresAt = expiryDayToIso(data.expiryDay);

      if (!expiresAt) {
        return null;
      }

      input.expiresAt = expiresAt;
    }
  }

  return input;
};
